// ---------------------------------------------------------------
// Live health checks — every system gets a verdict, healthy or not
// ---------------------------------------------------------------

import { attachAckSig } from './healthAck';

export const HEALTH_COLORS = {
  good: { bg: '#f0fdf4', border: '#22c55e', text: '#15803d' },
  warn: { bg: '#fffbeb', border: '#f59e0b', text: '#b45309' },
  bad: { bg: '#fef2f2', border: '#ef4444', text: '#b91c1c' },
  unknown: { bg: '#f8fafc', border: '#cbd5e1', text: '#64748b' },
};

const secondsToHm = (s) => `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m`;

// Engine coolant thresholds. Engines DON'T all run at the same temperature —
// most sit 90–110 °C, and many modern ones (VW/Audi especially) deliberately run
// a hot thermostat and cruise at 100–110 °C normally. A 105 °C limit therefore
// false-alarms on healthy cars. Real trouble starts well above that. A specific
// car can override these with tempWarnC / tempHotC on its device attributes.
export const TEMP_WARN_C = 112;
export const TEMP_HOT_C = 122;
export const tempLimits = (a = {}) => ({
  warn: Number(a.tempWarnC) > 0 ? Number(a.tempWarnC) : TEMP_WARN_C,
  hot: Number(a.tempHotC) > 0 ? Number(a.tempHotC) : TEMP_HOT_C,
});

export function assessHealth(a = {}) {
  const checks = [];
  const engineOn = a.ignition === true;
  const off = (title, zero) => ({ icon: '⏸️', title, verdict: `${zero} · unit off`, detail: 'Turn the unit on to read live values.', level: 'unknown' });

  // Check engine / fault codes
  if (a.io30 != null) {
    checks.push(a.io30 === 0
      ? { icon: '✅', title: 'Check Engine', verdict: 'No fault codes', detail: 'The engine computer reports no active problems.', level: 'good' }
      : { icon: '🚨', title: 'Check Engine', verdict: `${a.io30} fault code(s) active`, detail: a.io281 ? `Codes: ${a.io281}` : 'See the Alerts tab for details.', level: 'bad' });
  }

  // Coolant temperature (only meaningful with the engine running)
  if (a.io32 != null) {
    const t = a.io32;
    const lim = tempLimits(a);
    checks.push(
      !engineOn ? off('Engine Temperature', '0 °C')
      : t > lim.hot ? { icon: '🔥', title: 'Engine Temperature', verdict: `Overheating — ${t} °C`, detail: `Stop the vehicle when safe. Over the ${lim.hot} °C limit for this car.`, level: 'bad' }
      : t > lim.warn ? { icon: '🌡️', title: 'Engine Temperature', verdict: `Running hot — ${t} °C`, detail: `Above this car's ${lim.warn} °C threshold. Keep an eye on it.`, level: 'warn' }
      : t < 70 ? { icon: '❄️', title: 'Engine Temperature', verdict: `Warming up — ${t} °C`, detail: 'Engine has not reached operating temperature yet.', level: 'unknown' }
      : { icon: '✅', title: 'Engine Temperature', verdict: `Normal — ${t} °C`, detail: 'Most engines run 90–110 °C; this is within normal operating range.', level: 'good' }
    );
  }

  // Battery & charging
  if (a.power != null) {
    const v = a.power;
    const running = !!a.ignition;
    checks.push(
      running
        ? (v >= 13.0 ? { icon: '✅', title: 'Battery & Charging', verdict: `Charging — ${v.toFixed(1)} V`, detail: 'Alternator is charging normally (13.0–14.8 V expected while running; brief dips at idle are normal).', level: 'good' }
          : v >= 12.4 ? { icon: '⚠️', title: 'Battery & Charging', verdict: `Weak charging — ${v.toFixed(1)} V`, detail: 'Below 13.0 V with the engine on. Alternator or belt may need attention.', level: 'warn' }
          : { icon: '🪫', title: 'Battery & Charging', verdict: `Not charging — ${v.toFixed(1)} V`, detail: 'Voltage too low while running. Have the charging system checked.', level: 'bad' })
        : (v >= 12.4 ? { icon: '✅', title: 'Battery (engine off)', verdict: `Healthy — ${v.toFixed(1)} V`, detail: 'A rested battery above 12.4 V is in good shape.', level: 'good' }
          : v >= 11.8 ? { icon: '⚠️', title: 'Battery (engine off)', verdict: `Getting low — ${v.toFixed(1)} V`, detail: 'Below 12.4 V at rest. Battery may be aging or drained.', level: 'warn' }
          : { icon: '🪫', title: 'Battery (engine off)', verdict: `Very low — ${v.toFixed(1)} V`, detail: 'Risk of not starting. Charge or replace the battery.', level: 'bad' })
    );
  }

  // Fuel level
  if (a.io48 != null) {
    const f = a.io48;
    checks.push(
      f < 10 ? { icon: '⛽', title: 'Fuel', verdict: `Critical — ${f}%`, detail: 'Nearly empty.', level: 'bad' }
      : f < 25 ? { icon: '⛽', title: 'Fuel', verdict: `Low — ${f}%`, detail: 'Plan a fuel stop soon.', level: 'warn' }
      : { icon: '✅', title: 'Fuel', verdict: `${f}% in tank`, detail: 'Fuel level is fine.', level: 'good' }
    );
  }

  // ECU voltage (engine-running reading)
  if (a.io51 != null) {
    const v = a.io51 / 1000;
    checks.push(
      !engineOn ? off('Vehicle Computer (ECU)', '0 V')
      : v >= 11.5 ? { icon: '✅', title: 'Vehicle Computer (ECU)', verdict: `Stable — ${v.toFixed(2)} V`, detail: 'The engine computer is receiving healthy power.', level: 'good' }
      : { icon: '⚠️', title: 'Vehicle Computer (ECU)', verdict: `Low supply — ${v.toFixed(2)} V`, detail: 'ECU voltage below 11.5 V can cause electrical gremlins.', level: 'warn' }
    );
  }

  // Engine load snapshot
  if (a.io31 != null) {
    checks.push(!engineOn ? off('Engine Load', '0%')
      : { icon: 'ℹ️', title: 'Engine Load', verdict: `${a.io31}%`, detail: a.io31 > 85 ? 'Working very hard right now (towing, hill, or hard acceleration).' : 'Current workload on the engine.', level: a.io31 > 85 ? 'warn' : 'good' });
  }

  // Engine running time (only while running)
  if (engineOn && a.io42 != null && a.io42 > 0) {
    checks.push({ icon: '⏱️', title: 'Engine Running Time', verdict: secondsToHm(a.io42), detail: 'Time since this engine start.', level: 'good' });
  }

  // Warning-light history
  if (a.io43 != null && a.io43 > 0) {
    checks.push({ icon: '📋', title: 'Warning-Light History', verdict: `${a.io43} km with light on`, detail: 'The vehicle has previously been driven with the check-engine light on.', level: 'warn' });
  }

  // Tracker connection quality
  if (a.rssi != null) {
    checks.push({ icon: '📶', title: 'Tracker Signal', verdict: `${a.rssi}/5`, detail: a.rssi >= 3 ? 'Good cellular connection.' : 'Weak cellular signal — reports may be delayed.', level: a.rssi >= 3 ? 'good' : 'warn' });
  }

  attachAckSig(checks, a); // shared dismiss identity across tabs
  return checks;
}

export function healthScore(checks) {
  if (!checks.length) return null;
  if (checks.some((c) => c.level === 'bad')) return 'bad';
  if (checks.some((c) => c.level === 'warn')) return 'warn';
  return 'good';
}
